/**
 * TitleManager
 *
 * Admin UI for managing section titles used across exam modules and levels.
 * Responsibilities:
 * - Fetch, create, update, and delete section titles
 * - Filter and search by level/module; client-side pagination
 * - Manage optional audio material (base64 upload + preview, remove/replace)
 * - Support bulk selection and deletion
 * - Provide view, edit, create, and delete dialogs with optimistic UX
 *
 * Data Model (SectionTitle):
 * - id: numeric identifier
 * - level: A1–C2
 * - module: reading | listening | writing | speaking
 * - section: numeric section index (1–10)
 * - text: title text
 * - examType: list of exam providers (e.g., goethe, ösд, telc)
 * - material_*: optional audio material (binary base64, mime type, extension)
 * - createdAt/updatedAt: ISO timestamps
 *
 * Backend Endpoints (expected):
 * - GET    http://localhost:5001/api/section-titles
 * - POST   http://localhost:5001/api/section-titles
 * - PUT    http://localhost:5001/api/section-titles/:id
 * - DELETE http://localhost:5001/api/section-titles/:id
 * - DELETE /api/section-titles/bulk (frontend origin-relative) with { ids: number[] }
 *
 * Audio Handling:
 * - Accepts audio files (MP3/WAV/WebM/OGG) up to 10MB
 * - Converts to base64 for transport; prevents context menu and dragging in preview
 * - Existing audio can be kept, replaced, or removed during edit
 *
 * UI Notes:
 * - Pagination: 10 titles per page
 * - Bulk selection limited to current page rows
 * - Toast notifications on success/failure
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  X,
  Save,
  BookOpen,
  Eye,
  Loader2,
  FileAudio,
} from 'lucide-react';
import {
  convertFileToDataURL,
  extractBase64FromDataURL,
} from "@/lib/fileUtils";
import {
  CEFR_LEVELS,
  MODULES,
  VALID_AUDIO_TYPES,
} from "@/lib/constants";
import { formatDateOnly } from "@/lib/dateUtils";
import { ExamTypeMultiSelect } from '@/components/ui/exam-type-multi-select';
import {
  type SectionTitle,
  type SectionTitleFormData,
  getSectionTitlesList,
  getSectionTitleFull,
  createSectionTitle,
  updateSectionTitle,
  deleteSectionTitle,
  bulkDeleteSectionTitles,
} from "@/lib/sectionTitleApi";
import { logger } from '@/lib/logger';

// SectionTitle interface is imported from sectionTitleApi

/**
 * TitleFormData
 * UI form state for creating/updating titles, including transient file object.
 */
interface TitleFormData {
  level: "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
  module: "reading" | "listening" | "writing" | "speaking";
  section: number;
  text: string;
  examType: string[];
  material_type?: "audio" | null;
  weight?: number | null;
  file?: File;
}

/**
 * TitleManager component
 * Manages lifecycle and UI for section titles: listing, search/filter, CRUD, dialogs.
 */
// Helper: Validate weight value
const validateWeight = (weight: number | null | undefined): boolean => {
  if (weight === null || weight === undefined) return true;
  return weight >= 0 && weight <= 1;
};

// Helper: Parse weight from title
const parseWeight = (weight: string | number | null | undefined): number | null => {
  if (weight === null || weight === undefined) return null;
  const numWeight = typeof weight === "string" ? Number.parseFloat(weight) : Number(weight);
  return Number.isNaN(numWeight) ? null : numWeight;
};

// Helper: Build create request body
const buildCreateBody = (
  formData: TitleFormData,
  filePreview: string | null,
): SectionTitleFormData => {
  const body: SectionTitleFormData = {
    level: formData.level,
    module: formData.module,
    section: formData.section,
    text: formData.text,
    examType: formData.examType,
  };

  if (formData.material_type) {
    body.material_type = formData.material_type;
  }

  if (formData.weight !== null && formData.weight !== undefined) {
    body.weight = formData.weight;
  }

  if (formData.file && filePreview) {
    body.material_base64 = extractBase64FromDataURL(filePreview);
    body.material_extension = formData.file.name.split(".").pop();
    body.material_mime_type = formData.file.type;
  }

  return body;
};

// Helper: Build update request body
const buildUpdateBody = (
  formData: TitleFormData,
  filePreview: string | null,
  mediaRemoved: boolean,
  editingTitle: SectionTitle | null,
): SectionTitleFormData => {
  const body: SectionTitleFormData = {
    level: formData.level,
    module: formData.module,
    section: formData.section,
    text: formData.text,
    examType: formData.examType,
  };

  if (formData.weight !== null && formData.weight !== undefined) {
    body.weight = formData.weight;
  }

  if (formData.file && filePreview) {
    body.material_type = "audio";
    body.material_base64 = extractBase64FromDataURL(filePreview);
    body.material_extension = formData.file.name.split(".").pop();
    body.material_mime_type = formData.file.type;
  } else if (
    mediaRemoved ||
    (!formData.material_type && !formData.file && editingTitle?.material_type)
  ) {
    body.material_type = null;
    body.material_base64 = undefined;
    body.material_extension = undefined;
    body.material_mime_type = undefined;
  }

  return body;
};

// Helper: Prepare file preview from title material
const prepareFilePreview = (title: SectionTitle): string | null => {
  if (title.material_type === "audio") {
    // Prefer base64 data URL to avoid HTTP Range requests, which Chrome
    // handles more strictly than Safari and can cause playback failures.
    if (title.material_binary && title.material_mime_type) {
      return `data:${title.material_mime_type};base64,${title.material_binary}`;
    }
    if (title.material_url) return title.material_url;
  }
  return null;
};

// Helper: Handle create error
const handleCreateError = (error: any) => {
  if (error.message?.includes("already exists")) {
    toast.error('A section title with these parameters already exists');
  } else {
    logger.error("Error creating section title:", error);
    toast.error(`Failed to create section title: ${error.message}`);
  }
};

// Helper: Load titles with error handling
const loadTitlesHelper = async (
  setTitles: (titles: SectionTitle[]) => void,
  setLoading: (loading: boolean) => void,
) => {
  setLoading(true);
  try {
    const data = await getSectionTitlesList();
    setTitles(data);
  } catch (error) {
    logger.error("Error loading section titles:", error);
    toast.error('Failed to load section titles');
  } finally {
    setLoading(false);
  }
};

// Helper: Reset form to defaults
const createResetForm = (
  setFormData: (data: TitleFormData) => void,
  setFilePreview: (preview: string | null) => void,
  setMediaRemoved: (removed: boolean) => void,
) => {
  return () => {
    setFormData({
      level: "A1",
      module: "reading",
      section: 1,
      text: "",
      examType: ["goethe"],
      material_type: null,
      weight: null,
    });
    setFilePreview(null);
    setMediaRemoved(false);
  };
};

// Helper: Create delete title handler
const createDeleteTitleHandler = (
  deletingTitle: SectionTitle | null,
  setShowDeleteDialog: (show: boolean) => void,
  setDeletingTitle: (title: SectionTitle | null) => void,
  loadTitles: () => Promise<void>,
) => {
  return async () => {
    if (!deletingTitle) return;

    try {
      await deleteSectionTitle(deletingTitle.id);
      toast.success('Section title deleted successfully');
      setShowDeleteDialog(false);
      setDeletingTitle(null);
      await loadTitles();
    } catch (error: any) {
      logger.error("Error deleting section title:", error);
      toast.error(`Failed to delete section title: ${error.message}`);
    }
  };
};

// Helper: Create bulk delete handler
const createBulkDeleteHandler = (
  selectedTitles: number[],
  setSelectedTitles: (titles: number[] | ((prev: number[]) => number[])) => void,
  loadTitles: () => Promise<void>,
) => {
  return async () => {
    if (!confirm(`Are you sure you want to delete ${selectedTitles.length} titles?`)) {
      return;
    }

    try {
      await bulkDeleteSectionTitles(selectedTitles);
      toast.success(
        `Successfully deleted ${selectedTitles.length} section title${selectedTitles.length === 1 ? '' : 's'}`,
      );
      setSelectedTitles([]);
      await loadTitles();
    } catch (error: any) {
      logger.error("Error bulk deleting titles:", error);
      toast.error(`Failed to delete section title: ${error.message}`);
    }
  };
};

// Helper: Create view dialog opener
const createOpenViewDialog = (
  setViewingTitle: (title: SectionTitle | null) => void,
  setShowViewDialog: (show: boolean) => void,
) => {
  return async (title: SectionTitle) => {
    setViewingTitle(title);
    setShowViewDialog(true);

    try {
      const fullTitle = await getSectionTitleFull(title.id);
      setViewingTitle(fullTitle);
    } catch (error) {
      logger.error("Error loading full section title:", error);
      toast.error("Failed to load section title details");
    }
  };
};

// Helper: Create edit dialog opener
const createOpenEditDialog = (
  setEditingTitle: (title: SectionTitle | null) => void,
  setFormData: (data: TitleFormData | ((prev: TitleFormData) => TitleFormData)) => void,
  setFilePreview: (preview: string | null) => void,
  setMediaRemoved: (removed: boolean) => void,
) => {
  return (title: SectionTitle) => {
    setEditingTitle(title);
    const parsedWeight = parseWeight(title.weight);
    setFormData({
      level: title.level,
      module: title.module,
      section: title.section,
      text: title.text,
      examType: title.examType || ["goethe"],
      material_type: title.material_type || null,
      weight: parsedWeight,
    });
    setFilePreview(prepareFilePreview(title));
    setMediaRemoved(false);
  };
};

// Helper: Create file select handler
const createFileSelectHandler = (
  setFilePreview: (preview: string | null) => void,
  setFormData: (data: TitleFormData | ((prev: TitleFormData) => TitleFormData)) => void,
  setMediaRemoved: (removed: boolean) => void,
) => {
  return (file: File | null) => {
    if (!file) {
      setFilePreview(null);
      setFormData((prev) => ({
        ...prev,
        file: undefined,
        material_type: null,
      }));
      return;
    }

    if (!validateFileSize(file)) {
      toast.error('File size must be less than 10MB');
      return;
    }

    if (!validateFileType(file)) {
      toast.error('Please select a valid audio file (MP3, WAV, WebM, or OGG)');
      return;
    }

    setFormData((prev) => ({ ...prev, file, material_type: "audio" }));
    setMediaRemoved(false);

    convertFileToDataURL(file)
      .then(setFilePreview)
      .catch((error) => {
        logger.error("Error creating preview:", error);
        toast.error("Failed to create file preview");
      });
  };
};

// Helper: Create title handler
const createCreateTitleHandler = (
  formData: TitleFormData,
  filePreview: string | null,
  setSaving: (saving: boolean) => void,
  setShowCreateDialog: (show: boolean) => void,
  resetForm: () => void,
  loadTitles: () => Promise<void>,
) => {
  return async () => {
    if (!formData.text.trim()) {
      toast.error('Please enter a title text');
      return;
    }

    if (!validateWeight(formData.weight)) {
      toast.error('Weight must be between 0 and 1');
      return;
    }

    setSaving(true);
    try {
      const body = buildCreateBody(formData, filePreview);
      await createSectionTitle(body);
      toast.success('Section title created successfully');
      setShowCreateDialog(false);
      resetForm();
      await loadTitles();
    } catch (error: any) {
      handleCreateError(error);
    } finally {
      setSaving(false);
    }
  };
};

// Helper: Create update title handler
interface UpdateTitleHandlerParams {
  editingTitle: SectionTitle | null;
  formData: TitleFormData;
  filePreview: string | null;
  mediaRemoved: boolean;
  setSaving: (saving: boolean) => void;
  setEditingTitle: (title: SectionTitle | null) => void;
  resetForm: () => void;
  loadTitles: () => Promise<void>;
}

const createUpdateTitleHandler = (params: UpdateTitleHandlerParams) => {
  return async () => {
    if (!params.editingTitle || !params.formData.text.trim()) {
      toast.error('Please enter a title text');
      return;
    }

    if (!validateWeight(params.formData.weight)) {
      toast.error('Weight must be between 0 and 1');
      return;
    }

    params.setSaving(true);
    try {
      const body = buildUpdateBody(
        params.formData,
        params.filePreview,
        params.mediaRemoved,
        params.editingTitle,
      );
      await updateSectionTitle(params.editingTitle.id, body);
      toast.success('Section title updated successfully');
      params.setEditingTitle(null);
      params.resetForm();
      await params.loadTitles();
    } catch (error: any) {
      logger.error("Error updating section title:", error);
      toast.error(`Failed to update section title: ${error.message}`);
    } finally {
      params.setSaving(false);
    }
  };
};

// Helper: Validate file size
const validateFileSize = (file: File): boolean => {
  return file.size <= 10 * 1024 * 1024;
};

// Helper: Validate file type
const validateFileType = (file: File): boolean => {
  return VALID_AUDIO_TYPES.includes(file.type as any);
};

// Helper: Filter titles based on search and filters
const filterTitles = (
  titles: SectionTitle[],
  searchTerm: string,
  levelFilter: string,
  moduleFilter: string,
): SectionTitle[] => {
  return titles
    .filter((title) => {
      const matchesSearch =
        title.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        title.level.toLowerCase().includes(searchTerm.toLowerCase()) ||
        title.module.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLevel = levelFilter === "all" || title.level === levelFilter;
      const matchesModule = moduleFilter === "all" || title.module === moduleFilter;
      return matchesSearch && matchesLevel && matchesModule;
    })
    .sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
};

// Helper: Map CEFR level to tailwind color classes for the badge
const getLevelBadgeColor = (level: string): string => {
  switch (level) {
    case "A1":
    case "B1":
    case "B2":
    case "C1":
    case "C2":
      return "bg-primary/10 text-primary border-primary/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

// Helper: Map module to tailwind color classes for the badge
const getModuleBadgeColor = (module: string): string => {
  switch (module) {
    case "reading":
    case "listening":
    case "writing":
    case "speaking":
      return "bg-primary/10 text-primary border-primary/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

// Helper: Create delete dialog opener
const createOpenDeleteDialog = (
  setDeletingTitle: (title: SectionTitle | null) => void,
  setShowDeleteDialog: (show: boolean) => void,
) => {
  return (title: SectionTitle) => {
    setDeletingTitle(title);
    setShowDeleteDialog(true);
  };
};

// Helper: Create all handlers for TitleManager
interface CreateHandlersParams {
  formData: TitleFormData;
  filePreview: string | null;
  editingTitle: SectionTitle | null;
  mediaRemoved: boolean;
  deletingTitle: SectionTitle | null;
  selectedTitles: number[];
  setTitles: (titles: SectionTitle[]) => void;
  setLoading: (loading: boolean) => void;
  setFormData: (data: TitleFormData | ((prev: TitleFormData) => TitleFormData)) => void;
  setFilePreview: (preview: string | null) => void;
  setMediaRemoved: (removed: boolean) => void;
  setSaving: (saving: boolean) => void;
  setShowCreateDialog: (show: boolean) => void;
  setEditingTitle: (title: SectionTitle | null) => void;
  setDeletingTitle: (title: SectionTitle | null) => void;
  setShowDeleteDialog: (show: boolean) => void;
  setSelectedTitles: (titles: number[] | ((prev: number[]) => number[])) => void;
  setViewingTitle: (title: SectionTitle | null) => void;
  setShowViewDialog: (show: boolean) => void;
}

const createAllHandlers = (params: CreateHandlersParams) => {
  const loadTitles = () => loadTitlesHelper(params.setTitles, params.setLoading);
  const resetForm = createResetForm(params.setFormData, params.setFilePreview, params.setMediaRemoved);
  
  // Wrap async handlers to return void for onClick
  // These handlers are async but wrapped to return void for React event handlers
  return {
    loadTitles: () => {
      void loadTitles();
    },
    resetForm,
    handleCreateTitle: () => {
      void (createCreateTitleHandler(
        params.formData,
        params.filePreview,
        params.setSaving,
        params.setShowCreateDialog,
        resetForm,
        loadTitles,
      )());
    },
    handleUpdateTitle: () => {
      void (createUpdateTitleHandler({
        editingTitle: params.editingTitle,
        formData: params.formData,
        filePreview: params.filePreview,
        mediaRemoved: params.mediaRemoved,
        setSaving: params.setSaving,
        setEditingTitle: params.setEditingTitle,
        resetForm,
        loadTitles,
      })());
    },
    openDeleteDialog: createOpenDeleteDialog(params.setDeletingTitle, params.setShowDeleteDialog),
    handleDeleteTitle: () => {
      void (createDeleteTitleHandler(
        params.deletingTitle,
        params.setShowDeleteDialog,
        params.setDeletingTitle,
        loadTitles,
      )());
    },
    handleBulkDelete: () => {
      void (createBulkDeleteHandler(params.selectedTitles, params.setSelectedTitles, loadTitles)());
    },
    handleFileSelect: createFileSelectHandler(params.setFilePreview, params.setFormData, params.setMediaRemoved),
    openViewDialog: (title: SectionTitle) => {
      void (createOpenViewDialog(params.setViewingTitle, params.setShowViewDialog)(title));
    },
    openEditDialog: createOpenEditDialog(
      params.setEditingTitle,
      params.setFormData,
      params.setFilePreview,
      params.setMediaRemoved,
    ),
  };
};

// Helper: Create checkbox change handler for title selection
const createTitleSelectionHandler = (
  titleId: number,
  selectedTitles: number[],
  setSelectedTitles: (titles: number[] | ((prev: number[]) => number[])) => void,
) => {
  return (checked: boolean | string | undefined) => {
    if (checked === true) {
      setSelectedTitles([...selectedTitles, titleId]);
    } else {
      setSelectedTitles(selectedTitles.filter((id) => id !== titleId));
    }
  };
};

// Helper: Render exam type badges
const renderExamTypeBadges = (examTypes: string[] | null | undefined) => {
  const types = examTypes || ["goethe"];
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((examType) => (
        <Badge
          key={examType}
          variant="secondary"
          className="text-xs"
        >
          {examType.toUpperCase()}
        </Badge>
      ))}
    </div>
  );
};


// Helper: Create table body renderer
interface RenderTableBodyParams {
  loading: boolean;
  filteredTitles: SectionTitle[];
  currentTitles: SectionTitle[];
  selectedTitles: number[];
  setSelectedTitles: (titles: number[] | ((prev: number[]) => number[])) => void;
  openViewDialog: (title: SectionTitle) => void;
  openEditDialog: (title: SectionTitle) => void;
  openDeleteDialog: (title: SectionTitle) => void;
}

const createRenderTableBody = (params: RenderTableBodyParams) => {
  return () => {
    if (params.loading) {
      return (
        <TableRow>
          <TableCell colSpan={9} className="text-center py-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2">
                Loading section titles...
              </span>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    if (params.filteredTitles.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} className="text-center py-8">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <BookOpen className="h-8 w-8" />
              <p className="font-ui text-lg font-medium">
                No section titles found
              </p>
              <p className="font-ui text-sm">
                Create your first section title to get started.
              </p>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    return params.currentTitles.map((title) => {
      const handleView = () => params.openViewDialog(title);
      const handleEdit = () => params.openEditDialog(title);
      const handleDelete = () => params.openDeleteDialog(title);
      
      return (
        <TableRow key={title.id}>
          <TableCell>
            <Checkbox
              checked={params.selectedTitles.includes(title.id)}
              onCheckedChange={createTitleSelectionHandler(
                title.id,
                params.selectedTitles,
                params.setSelectedTitles,
              )}
            />
          </TableCell>
          <TableCell className="w-20">
            <Badge
              className={`text-xs whitespace-nowrap ${getLevelBadgeColor(title.level)}`}
            >
              {title.level}
            </Badge>
          </TableCell>
          <TableCell className="w-32">
            <Badge
              className={`text-xs whitespace-nowrap ${getModuleBadgeColor(title.module)}`}
            >
              {title.module.toUpperCase()}
            </Badge>
          </TableCell>
          <TableCell className="w-24">
            <Badge
              variant="outline"
              className="text-xs whitespace-nowrap"
            >
              Sec {title.section}
            </Badge>
          </TableCell>
          <TableCell className="w-32">
            {renderExamTypeBadges(title.examType)}
          </TableCell>
          <TableCell className="w-80 max-w-80">
            <div className="min-w-0">
              <p className="font-ui font-medium truncate text-sm leading-tight">
                {title.text}
              </p>
            </div>
          </TableCell>
          <TableCell className="font-ui w-32 text-xs text-muted-foreground">
            {formatDateOnly(title.createdAt)}
          </TableCell>
          <TableCell className="font-ui w-32 text-xs text-muted-foreground">
            {formatDateOnly(title.updatedAt)}
          </TableCell>
          <TableCell className="w-32">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleView}
                className="h-8 w-8"
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleEdit}
                className="h-8 w-8"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-8 w-8 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };
};

export default function TitleManager() {
  const [titles, setTitles] = useState<SectionTitle[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [moduleFilter, setModuleFilter] = useState<string>("all");
  const [selectedTitles, setSelectedTitles] = useState<number[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingTitle, setEditingTitle] = useState<SectionTitle | null>(null);
  const [viewingTitle, setViewingTitle] = useState<SectionTitle | null>(null);
  const [deletingTitle, setDeletingTitle] = useState<SectionTitle | null>(null);
  const [formData, setFormData] = useState<TitleFormData>({
    level: "A1",
    module: "reading",
    section: 1,
    text: "",
    examType: ["goethe"],
    material_type: null,
    weight: null,
  });
  const [saving, setSaving] = useState(false);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [mediaRemoved, setMediaRemoved] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const titlesPerPage = 10;

  // Use constants from lib/constants
  const levels = CEFR_LEVELS;
  const modules = MODULES;
  const sections = Array.from({ length: 10 }, (_, i) => i + 1);

  // Load titles on component mount
  useEffect(() => {
    loadTitles();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, levelFilter, moduleFilter]);

  // Create all handlers
  const {
    loadTitles,
    resetForm,
    handleCreateTitle,
    handleUpdateTitle,
    openDeleteDialog,
    handleDeleteTitle,
    handleBulkDelete,
    handleFileSelect,
    openViewDialog,
    openEditDialog,
  } = createAllHandlers({
    formData,
    filePreview,
    editingTitle,
    mediaRemoved,
    deletingTitle,
    selectedTitles,
    setTitles,
    setLoading,
    setFormData,
    setFilePreview,
    setMediaRemoved,
    setSaving,
    setShowCreateDialog,
    setEditingTitle,
    setDeletingTitle,
    setShowDeleteDialog,
    setSelectedTitles,
    setViewingTitle,
    setShowViewDialog,
  });

  // Filter titles based on search term and filters
  const filteredTitles = filterTitles(titles, searchTerm, levelFilter, moduleFilter);

  // Pagination logic
  const totalPages = Math.ceil(filteredTitles.length / titlesPerPage);
  const startIndex = (currentPage - 1) * titlesPerPage;
  const endIndex = startIndex + titlesPerPage;
  const currentTitles = filteredTitles.slice(startIndex, endIndex);

  // Render table body content
  const renderTableBody = createRenderTableBody({
    loading,
    filteredTitles,
    currentTitles,
    selectedTitles,
    setSelectedTitles,
    openViewDialog,
    openEditDialog,
    openDeleteDialog,
  });

  // Helper: Render create/edit form dialog
  const renderFormDialog = () => (
    <Dialog
      open={!!(showCreateDialog || editingTitle)}
      onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setEditingTitle(null);
          resetForm();
        }
      }}
    >
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-ui">
            {editingTitle ? 'Edit Section Title' : 'Create Section Title'}
          </DialogTitle>
          <DialogDescription className="font-ui">
            {editingTitle
              ? 'Update the section title details below'
              : 'Fill in the details to create a new section title'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="level" className="font-ui">Level</Label>
              <Select
                value={formData.level}
                onValueChange={(
                  value: "A1" | "A2" | "B1" | "B2" | "C1" | "C2",
                ) => setFormData({ ...formData, level: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {levels.map((level) => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="module" className="font-ui">Module</Label>
              <Select
                value={formData.module}
                onValueChange={(
                  value: "reading" | "listening" | "writing" | "speaking",
                ) => setFormData({ ...formData, module: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((module) => (
                    <SelectItem key={module.value} value={module.value}>
                      {module.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="section" className="font-ui">
                Section
              </Label>
              <Select
                value={formData.section.toString()}
                onValueChange={(value) =>
                  setFormData({ ...formData, section: Number.parseInt(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((section) => (
                    <SelectItem key={section} value={section.toString()}>
                      Section {section}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="text" className="font-ui">Title Text</Label>
            <Textarea
              id="text"
              value={formData.text}
              onChange={(e) =>
                setFormData({ ...formData, text: e.target.value })
              }
              placeholder="Enter the section title text..."
              rows={3}
            />
          </div>
          <div>
            <ExamTypeMultiSelect
              label="Exam Types"
              selectedTypes={formData.examType}
              onChange={(updatedTypes) =>
                setFormData({ ...formData, examType: updatedTypes })
              }
              htmlId="examType"
            />
          </div>
          <div>
            <Label htmlFor="weight" className="font-ui">Weight (Optional)</Label>
            <Input
              id="weight"
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={
                formData.weight !== null && formData.weight !== undefined
                  ? formData.weight.toString()
                  : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  weight: value === "" ? null : Number.parseFloat(value),
                });
              }}
              placeholder="e.g., 0.4"
              className="mt-1"
            />
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Weight for scoring (0-1, e.g., 0.4 = 40% of total score)
            </p>
          </div>
          <div>
            <Label htmlFor="audioFile" className="font-ui">
              Audio Material (Optional)
            </Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  id="audioFile"
                  type="file"
                  accept="audio/*"
                  onChange={(e) =>
                    handleFileSelect(e.target.files?.[0] || null)
                  }
                  className="flex-1"
                />
                {filePreview && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleFileSelect(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {filePreview && (
                <section
                  className="p-4 bg-muted rounded-lg"
                  aria-label="Audio preview"
                >
                  <audio
                    src={filePreview}
                    controls
                    className="w-full"
                    preload="metadata"
                  >
                    <track kind="captions" src="" label="No captions available" default={false} />
                    Your browser does not support the audio element.
                  </audio>
                </section>
              )}
              {editingTitle?.material_type === "audio" &&
                !filePreview &&
                !mediaRemoved &&
                (editingTitle?.material_url ||
                  editingTitle?.material_binary) && (
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileAudio className="h-4 w-4" />
                        <span className="font-ui">
                          Existing audio material
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            material_type: null,
                          }));
                          setFilePreview(null);
                          setMediaRemoved(true);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" />
                        <span className="font-ui">Remove</span>
                      </Button>
                    </div>
                    <audio
                      src={
                        editingTitle.material_url ||
                        (editingTitle.material_binary &&
                        editingTitle.material_mime_type
                          ? `data:${editingTitle.material_mime_type};base64,${editingTitle.material_binary}`
                          : undefined)
                      }
                      controls
                      className="w-full"
                      preload="metadata"
                    >
                      <track kind="captions" src="" label="No captions available" default={false} />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setEditingTitle(null);
                resetForm();
              }}
            >
              <span className="font-ui">Cancel</span>
            </Button>
            <Button
              onClick={editingTitle ? handleUpdateTitle : handleCreateTitle}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="font-ui">
                    {editingTitle ? 'Updating...' : 'Creating...'}
                  </span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  <span className="font-ui">
                    {editingTitle ? 'Update Title' : 'Create Title'}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Helper: Render view dialog
  const renderViewDialog = () => (
    <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-ui">Section Title Preview</DialogTitle>
          <DialogDescription className="font-ui">View section title details</DialogDescription>
        </DialogHeader>

        {viewingTitle && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge
                className={`text-xs ${getLevelBadgeColor(viewingTitle.level)}`}
              >
                {viewingTitle.level}
              </Badge>
              <Badge
                className={`text-xs ${getModuleBadgeColor(viewingTitle.module)}`}
              >
                {viewingTitle.module.toUpperCase()}
              </Badge>
              <Badge variant="outline" className="text-xs">
                Section {viewingTitle.section}
              </Badge>
            </div>

            <div>
              <Label className="font-ui">Title Text</Label>
              <p className="font-ui text-sm mt-1 p-3 bg-muted rounded-md">
                {viewingTitle.text}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-ui">Level</Label>
                <p className="font-ui text-sm mt-1">{viewingTitle.level}</p>
              </div>
              <div>
                <Label className="font-ui">Module</Label>
                <p className="font-ui text-sm mt-1">{viewingTitle.module}</p>
              </div>
              <div>
                <Label className="font-ui">Section</Label>
                <p className="font-ui text-sm mt-1">{viewingTitle.section}</p>
              </div>
              <div>
                <Label className="font-ui">Weight</Label>
                <p className="font-ui text-sm mt-1">
                  {viewingTitle.weight !== null &&
                  viewingTitle.weight !== undefined
                    ? `${(Number(viewingTitle.weight) * 100).toFixed(2)}%`
                    : 'Not set (equal weight)'}
                </p>
              </div>
              <div>
                <Label className="font-ui">Exam Types</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(viewingTitle.examType || ["goethe"]).map((examType) => (
                    <Badge
                      key={examType}
                      variant="secondary"
                      className="text-xs"
                    >
                      {examType.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-ui">Created</Label>
                <p className="font-ui text-sm mt-1">
                  {new Date(viewingTitle.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <Label className="font-ui">Modified</Label>
                <p className="font-ui text-sm mt-1">
                  {new Date(viewingTitle.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {viewingTitle.material_type &&
              (viewingTitle.material_url || viewingTitle.material_binary) && (
                <div>
                  <Label className="font-ui">Audio Material</Label>
                  <div className="mt-2">
                    <section
                      className="p-4 bg-muted rounded-lg"
                      aria-label="Audio preview"
                    >
                      <audio
                        src={
                          viewingTitle.material_url ||
                          (viewingTitle.material_binary
                            ? `data:${viewingTitle.material_mime_type || "audio/mpeg"};base64,${viewingTitle.material_binary}`
                            : undefined)
                        }
                        controls
                        className="w-full"
                        preload="metadata"
                      >
                        <track kind="captions" src="" label="No captions available" default={false} />
                        Your browser does not support the audio element.
                      </audio>
                    </section>
                  </div>
                </div>
              )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowViewDialog(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              setShowViewDialog(false);
              if (viewingTitle) {
                openEditDialog(viewingTitle);
              }
            }}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Helper: Render delete dialog
  const renderDeleteDialog = () => (
    <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Section Title
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this section title? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {deletingTitle && (
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Badge
                className={`text-xs ${getLevelBadgeColor(deletingTitle.level)}`}
              >
                {deletingTitle.level}
              </Badge>
              <Badge
                className={`text-xs ${getModuleBadgeColor(deletingTitle.module)}`}
              >
                {deletingTitle.module.toUpperCase()}
              </Badge>
              <Badge variant="outline" className="text-xs">
                Section {deletingTitle.section}
              </Badge>
            </div>
            <p className="font-medium">{deletingTitle.text}</p>
            <p className="text-sm text-muted-foreground mt-1">
              Created:{" "}
              {new Date(deletingTitle.createdAt).toLocaleDateString()}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setShowDeleteDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteTitle}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Title
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      {/* Title Form Modal */}
      {renderFormDialog()}

      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-ui text-xl font-bold flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Section Titles Management
              </CardTitle>
              <CardDescription className="font-ui">
                Manage section titles for exam modules and levels
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="font-ui">Add Title</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Bar */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by level, module, section, or text..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-32">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Levels
                </SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All Modules
                </SelectItem>
                {modules.map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    {module.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions */}
          {selectedTitles.length > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-muted rounded-lg">
              <span className="font-ui text-sm text-muted-foreground">
                {selectedTitles.length} title(s) selected
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}

          {/* Titles Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-fixed min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          selectedTitles.length === currentTitles.length &&
                          currentTitles.length > 0
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedTitles(currentTitles.map((t) => t.id));
                          } else {
                            setSelectedTitles([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead className="w-20">Level</TableHead>
                    <TableHead className="w-32">Module</TableHead>
                    <TableHead className="w-24">Section</TableHead>
                    <TableHead className="w-32">Exam Types</TableHead>
                    <TableHead className="w-80">Title Text</TableHead>
                    <TableHead className="w-32">Created</TableHead>
                    <TableHead className="w-32">Modified</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{renderTableBody()}</TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination Controls */}
          {filteredTitles.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {currentTitles.length} of {filteredTitles.length}{" "}
                section titles (Page {currentPage} of {totalPages})
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span className="font-ui">
              Showing {filteredTitles.length} of {titles.length} section titles
            </span>
            <div className="flex gap-4">
              {levels.map((level) => (
                <span key={level}>
                  {level}: {titles.filter((t) => t.level === level).length}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Dialog */}
      {renderViewDialog()}

      {/* Delete Confirmation Dialog */}
      {renderDeleteDialog()}
    </div>
  );
}
